// material-ui
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useMemo, useState } from 'react';
import { EventContext } from '../../event/eventContext';

// ==============================|| CUSTOM BRAND LOGO ||============================== //

const FALLBACK = '/ClubAstraLogoSide.png';

// Sidebar logos fit a box instead of a fixed height: wide logos stop at the width cap (Club Astra
// renders exactly as before), compact or stacked logos grow taller so they don't look tiny.
const DRAWER_MAX_HEIGHT = 56;
const DRAWER_MAX_WIDTH = 180;

function initialsFor(name) {
  return (
    String(name || 'Brand')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase() || 'B'
  );
}

export default function LogoMain({ drawer = false }) {
  // Resilient read (no useEvent throw): render the resolved brand's uploaded logo when set, else the
  // bundled Club Astra asset. onError falls back too (offline / stale URL) so the header never breaks.
  const ev = useContext(EventContext);
  const brand = (ev && ev.brand) || {};
  const src = brand.logo || brand.icon || FALLBACK;
  const fallbackSrc = brand.icon && src !== brand.icon ? brand.icon : FALLBACK;
  const label = brand.shortName || brand.name || 'Brand';
  const initials = useMemo(() => initialsFor(label), [label]);
  const [activeSrc, setActiveSrc] = useState(src);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setActiveSrc(src);
    setImageFailed(false);
  }, [src]);

  return (
    <Box
      sx={{
        height: drawer ? 'auto' : 36,
        minHeight: drawer ? 36 : undefined,
        minWidth: 88,
        display: 'inline-grid',
        placeItems: 'center'
      }}
    >
      {!imageFailed && activeSrc ? (
        <Box
          component="img"
          src={activeSrc}
          alt={brand.name || 'Brand logo'}
          onError={() => {
            if (fallbackSrc && activeSrc !== fallbackSrc) {
              setActiveSrc(fallbackSrc);
              return;
            }
            setImageFailed(true);
          }}
          sx={
            drawer
              ? {
                  gridArea: '1 / 1',
                  width: 'auto',
                  height: 'auto',
                  maxHeight: DRAWER_MAX_HEIGHT,
                  maxWidth: DRAWER_MAX_WIDTH,
                  display: 'block'
                }
              : {
                  gridArea: '1 / 1',
                  height: 36,
                  maxWidth: 180,
                  display: 'block',
                  objectFit: 'contain'
                }
          }
        />
      ) : null}
      {imageFailed && (
        <Typography
          aria-hidden="true"
          sx={{
            gridArea: '1 / 1',
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1,
            color: 'text.primary'
          }}
        >
          {label || initials}
        </Typography>
      )}
    </Box>
  );
}

LogoMain.propTypes = { drawer: PropTypes.bool };
